/**
 * CompactDmlHeaderTarget — the compact popover's UPDATE / DELETE header text,
 * `VERB target`, with the target name as the Change table picker (chevron on
 * the name). The header IS the popover's target row, so the swap lives where
 * the table is named instead of in a separate row. Shapes the swap can't
 * rewrite — `UPDATE p … FROM t p` (alias target) and `DELETE t1 FROM …`
 * (targets named before FROM) — keep the name as plain text, the same rules
 * the full view's "Change table" button is disabled by.
 */

import type { ParseOptions } from '../../../sql-grammar/model';
import type { VqeEditContext } from '../edit/editContext';
import { DmlChangeTablePicker } from '../render/common/DmlChangeTablePicker';
import type { DeleteOutline } from '../render/delete/extractDeleteOutline';
import type { UpdateOutline } from '../render/update/extractUpdateOutline';
import { buildAliasCandidatesFromText, resolveDmlTargetAlias } from '../render/update/resolveDmlTargetAlias';
import { vqeCopy } from '../ux/vqeCopy';

/**
 * SQL Server `UPDATE p … FROM Production.Product p`: the written target is an
 * alias of a FROM table. Rewriting that token would point the UPDATE at a new
 * table cross-joined with the old one, so Change table is blocked for it — the
 * same rule as UpdateRenderer's targetIsFromAlias.
 */
function isUpdateTargetFromAlias(
  bufferText: string,
  targetText: string,
  fromStart: number | null,
  fromEnd: number | null,
  options: ParseOptions,
): boolean {
  if (fromStart === null || fromEnd === null) return false;
  const resolved = resolveDmlTargetAlias(
    targetText,
    buildAliasCandidatesFromText(bufferText, fromStart, fromEnd, options),
  );
  return (
    resolved.relation !== null &&
    resolved.alias !== null &&
    resolved.alias.toLowerCase() === targetText.trim().toLowerCase()
  );
}

export interface CompactDmlHeaderTargetProps {
  /** Header summary — `VERB target` (the verb is everything before the first space). */
  readonly summary: string;
  readonly dml:
    | { readonly intent: 'update'; readonly outline: UpdateOutline }
    | { readonly intent: 'delete'; readonly outline: DeleteOutline };
  readonly bufferText: string;
  readonly options: ParseOptions;
  readonly editContext: VqeEditContext | null;
}

export function CompactDmlHeaderTarget({ summary, dml, bufferText, options, editContext }: CompactDmlHeaderTargetProps) {
  const split = summary.indexOf(' ');
  const verb = summary.slice(0, split);
  const target = summary.slice(split + 1);
  let blockedReason: string | undefined;
  if (dml.intent === 'delete') {
    if (dml.outline.preFromTargetText !== null) blockedReason = vqeCopy.dmlTarget.disabled.targetList;
  } else if (
    dml.outline.targetText !== null &&
    isUpdateTargetFromAlias(bufferText, dml.outline.targetText, dml.outline.fromStart, dml.outline.fromEnd, options)
  ) {
    blockedReason = vqeCopy.dmlTarget.disabled.aliasTarget;
  }
  return (
    <>
      <span className="shrink-0">{verb}</span>{' '}
      <DmlChangeTablePicker
        target={dml.intent}
        variant="inline"
        label={target}
        editContext={editContext ?? undefined}
        blockedReason={blockedReason}
      />
    </>
  );
}
